import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ClearIcon from '@mui/icons-material/Clear';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import { Box, IconButton, ListItem, ListItemButton, TextField, Tooltip, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import { Dashboard } from '@protos/v2/dashboard';
import { FluxIconButton } from '@shared/components/FluxIconButton';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import theme from '@shared/themes/darkTheme';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { MAX_DASHBOARD_NAME_LENGTH } from './constants';
import { useDashboardList } from './useDashboardList';

type Props = {
  dashboard: Dashboard;
  dashboardToDelete: Dashboard | null;
  hideIcons?: boolean;
  setAnchorEl: (anchor: HTMLElement | null) => void;
  setDashboardToShare: (dashboard: Dashboard | null) => void;
  setDashboardToDelete: (dashboard: Dashboard | null) => void;
  freemiumPath?: string;
};

export const DashboardItem = ({
  dashboard,
  dashboardToDelete,
  hideIcons = false,
  setAnchorEl,
  setDashboardToShare,
  setDashboardToDelete,
  freemiumPath = '',
}: Props) => {
  const navigate = useNavigate();
  const {
    updateDashboard,
    deleteDashboard,
    addDashboard,
    currentDashboard: selectedDashboard,
    allDashboards,
    setCurrentDashboardById,
    clearCurrentDashboard,
    selectedFreemiumDashboard,
    setSelectedFreemiumDashboard,
  } = useDashboardContext();
  const {
    onDashboardClick,
    onDashboardDoubleClick,
    onEditDashboardName,
    onConfirmEditingChanges,
    isInvalidEditingDashboardName,
    isEditingDashboard,
    editingDashboardName,
    setEditingDashboardName,
    setIsEditingDashboard,
  } = useDashboardList(updateDashboard, deleteDashboard, addDashboard, setCurrentDashboardById, navigate, allDashboards, selectedDashboard);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dashboard.id });
  const [isHovered, setIsHovered] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <ListItem disablePadding ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ListItemButton
        selected={selectedDashboard ? dashboard.id === +selectedDashboard.id : selectedFreemiumDashboard === dashboard.id}
        onClick={() => {
          if (freemiumPath) {
            navigate(freemiumPath);
            clearCurrentDashboard();
            setSelectedFreemiumDashboard(dashboard.id);
            return;
          }
          if (!isEditingDashboard.isEditing || (isEditingDashboard.isEditing && isEditingDashboard.id !== dashboard.id))
            onDashboardClick(dashboard.id);
        }}
        onDoubleClick={event => {
          if (!isEditingDashboard.isEditing || (isEditingDashboard.isEditing && isEditingDashboard.id !== dashboard.id))
            onDashboardDoubleClick(dashboard.id, event);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          padding: 1,
          '&:hover': { backgroundColor: '#142535 !important' },
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          alignItems: 'center',
        }}
      >
        {!isEditingDashboard.isEditing || (isEditingDashboard.isEditing && isEditingDashboard.id !== dashboard.id) ? (
          <Link
            component={RouterLink}
            to={isDragging ? '#' : `/dashboard/${dashboard.id}`}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'row',
              flex: 1,
              alignItems: 'center',
              gap: 1,
            }}
          >
            <DashboardIcon sx={{ fontSize: 15, marginRight: 1 }} />
            <Tooltip title={dashboard.name?.length > MAX_DASHBOARD_NAME_LENGTH ? dashboard.name : undefined}>
              <Typography variant="body1" sx={{ fontSize: 11, mb: 0.2 }}>
                {dashboard.name?.length > MAX_DASHBOARD_NAME_LENGTH ? dashboard.name?.slice(0, MAX_DASHBOARD_NAME_LENGTH) + '..' : dashboard.name}
              </Typography>
            </Tooltip>

            {!hideIcons && !isDragging && (isHovered || (dashboardToDelete && dashboardToDelete.id === dashboard.id)) && (
              <Box display="flex" alignItems="center" gap={2} marginLeft="auto">
                <FluxIconButton
                  Icon={EditIcon}
                  label="Edit Dashboard Name"
                  onClick={event => {
                    event.preventDefault();
                    onEditDashboardName(dashboard.id, event);
                  }}
                  sx={{
                    fontSize: 15,
                    padding: 0,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
                <FluxIconButton
                  Icon={ShareIcon}
                  label="Share Dashboard"
                  onClick={event => {
                    event.preventDefault();
                    event?.stopPropagation();
                    setDashboardToShare(dashboard);
                  }}
                  sx={{
                    fontSize: 15,
                    padding: 0,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
                <FluxIconButton
                  Icon={DeleteIcon}
                  label="Delete Dashboard"
                  onClick={event => {
                    event.preventDefault();
                    event?.stopPropagation();
                    setAnchorEl(event.currentTarget);
                    setDashboardToDelete(dashboard);
                  }}
                  sx={{
                    fontSize: 15,
                    padding: 0,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                />
              </Box>
            )}
          </Link>
        ) : (
          <Box display="flex" alignItems="center" flex={1} justifyContent="space-between">
            <TextField
              size="small"
              margin="normal"
              variant="outlined"
              sx={{
                maxWidth: 120,
                '& .MuiInputBase-input': {
                  fontSize: 11,
                  padding: 0.5,
                  '&:focus': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputBase-root': {
                  borderRadius: 0,
                },
                marginTop: 0,
                marginBottom: 0,
              }}
              value={editingDashboardName}
              onChange={e => setEditingDashboardName(e.target.value)}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isInvalidEditingDashboardName) onConfirmEditingChanges();
                }
              }}
              error={isInvalidEditingDashboardName}
            />
            <Box display="flex" alignItems="center" gap={1} marginLeft="auto">
              <Tooltip title="Discard Changes">
                <IconButton
                  sx={{ fontSize: 15, padding: 0 }}
                  onClick={event => {
                    event.stopPropagation();
                    setIsEditingDashboard({ isEditing: false, id: null });
                    setEditingDashboardName(undefined);
                  }}
                >
                  <ClearIcon sx={{ fontSize: 15, padding: 0, color: theme.palette.background.sell, '&:hover': { color: 'white' } }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Confirm Changes">
                <IconButton
                  sx={{ fontSize: 15, padding: 0 }}
                  onClick={event => {
                    event.stopPropagation();
                    onConfirmEditingChanges();
                  }}
                  disabled={isInvalidEditingDashboardName}
                >
                  <DoneIcon
                    sx={{
                      fontSize: 15,
                      padding: 0,
                      color: isInvalidEditingDashboardName ? 'grey' : theme.palette.background.buy,
                      '&:hover': { color: 'white' },
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
};
